<template>
  <ubi-page-container class="ContactsPrograms" wide-main-content>
    <no-contact-manager v-if="!possedeContactManager && !possedeCallTracking" />

    <template v-else-if="!canAccessPage">
      <ubi-panel>
        <p>{{ $t('contactsPrograms.accessDenied') }}</p>
      </ubi-panel>
    </template>

    <template v-else>
      <div class="ContactsPrograms__content">
        <api-search-form
          ref="apiSearchForm"
          :key="$route.fullPath"
          route="contact/contacts-programs"
          :business-domain="sitimmoUser.univers.toLowerCase()"
          :user-context="userContext"
          @search="handleSearchFromApi"
          @ready="handleSearchFormReady"
        />

        <ubi-panel
          class="contacts-programs-panel"
          size="sm"
          full-width
          :title="$t('contactsPrograms.contactsProgramsTitle')"
        >
          <el-button
            slot="header-actions"
            type="mini"
            :disabled="!excelData.length"
          >
            <downloadexcel
              :data="excelData"
              type="xls"
              name="contacts_par_programmes.xls"
            >
              {{ $t('contacts.exportResults') }}
            </downloadexcel>
          </el-button>

          <div
            v-loading="loading"
            class="contacts-programs-table-wrap"
          >
            <el-table
              :data="formattedTable.rows"
              :row-class-name="tableRowClassName"
              border
              class="contacts-programs-table"
              style="width: 100%"
            >
              <el-table-column
                prop="diffuseur"
                fixed
                :label="$t('contactsPrograms.colonneDiffuseur')"
                min-width="180"
              />
              <el-table-column
                v-for="programmeName in formattedTable.programNames"
                :key="programmeName"
                :prop="programmeName"
                :label="programmeName"
                min-width="130"
                align="center"
              />
              <el-table-column
                prop="totalRow"
                fixed="right"
                :label="$t('contactsPrograms.colonneTotal')"
                width="120"
                align="center"
              />
            </el-table>
          </div>
        </ubi-panel>
      </div>
    </template>
  </ubi-page-container>
</template>

<script>
import UbiPanel from '../../components/Panel'
import { getContactsProgrammes } from '@/services/contacts'
import UbiPageContainer from '@/components/PageContainer.vue'
import downloadexcel from 'vue-json-excel'
import ApiSearchForm from '@/components/search/ApiSearchForm.vue'
import NoContactManager from '@/pages/contacts/NoContactManager.vue'
import { convertApiFiltersToStore } from '@/utils/filterConverter'
import { getFromSnake } from '@/helpers/functions/getFromSnake'
import {
  buildExcelExportRows,
  buildFormattedTableRows,
  canAccessContactsPrograms
} from '@/utils/contactsProgramsMatrix'
import { mapGetters } from 'vuex'

export default {
  name: 'contacts-programs',
  components: {
    NoContactManager,
    ApiSearchForm,
    downloadexcel,
    UbiPageContainer,
    UbiPanel
  },
  data () {
    return {
      loading: false,
      tableData: [],
      localFilters: {
        contactAAfficher: null,
        beforeContacts: null,
        afterContacts: null,
        search: null
      },
      searchFormReady: false
    }
  },
  computed: {
    ...mapGetters(['getSitimmoUser']),
    sitimmoUser () {
      return this.getSitimmoUser
    },
    userContext () {
      return {
        userId: this.sitimmoUser.id,
        univers: this.sitimmoUser.univers,
        metier: this.sitimmoUser.metier,
        betaTesteur: this.sitimmoUser.betaTesteur || false
      }
    },
    possedeContactManager () {
      return getFromSnake(this.sitimmoUser, 'possede_contact_manager')
    },
    possedeCallTracking () {
      return getFromSnake(this.sitimmoUser, 'possede_call_tracking')
    },
    canAccessPage () {
      return canAccessContactsPrograms(this.sitimmoUser)
    },
    formattedTable () {
      return buildFormattedTableRows(
        this.tableData,
        this.$t('contactsPrograms.colonneTotal')
      )
    },
    excelData () {
      return buildExcelExportRows(
        this.tableData,
        this.$t('contactsPrograms.colonneTotal')
      )
    }
  },
  methods: {
    tableRowClassName ({ row }) {
      return row.isTotalRow ? 'contacts-programs-table__total-row' : ''
    },
    handleSearchFormReady () {
      this.searchFormReady = true
      this.fetchList()
    },
    handleSearchFromApi (filters) {
      const converted = convertApiFiltersToStore(filters, { metier: this.sitimmoUser.metier })
      const { beforeContacts, afterContacts } = this.resolveContactsProgramsDates(filters, converted)
      const search = converted.search != null ? String(converted.search).trim() : ''
      this.localFilters = {
        contactAAfficher: converted.contactAAfficher || null,
        beforeContacts,
        afterContacts,
        search: search || null
      }
      this.fetchList()
    },
    /**
     * Dates CM : eventMapping (beforeContacts/afterContacts) ou repli dateRange, sans toucher au converter global.
     */
    resolveContactsProgramsDates (apiFilters, converted) {
      if (converted.beforeContacts != null || converted.afterContacts != null) {
        return {
          beforeContacts: converted.beforeContacts != null ? converted.beforeContacts : null,
          afterContacts: converted.afterContacts != null ? converted.afterContacts : null
        }
      }
      if (Array.isArray(apiFilters.dateRange) && apiFilters.dateRange.length >= 2) {
        return {
          beforeContacts: apiFilters.dateRange[0] || null,
          afterContacts: apiFilters.dateRange[1] || null
        }
      }
      return { beforeContacts: null, afterContacts: null }
    },
    async fetchList () {
      if (!this.canAccessPage) {
        return
      }
      this.loading = true
      const filters = {}
      if (this.localFilters.contactAAfficher) {
        filters.contactAAfficher = this.localFilters.contactAAfficher
      }
      if (this.localFilters.beforeContacts) {
        filters.startDate = this.localFilters.beforeContacts
      }
      if (this.localFilters.afterContacts) {
        filters.endDate = this.localFilters.afterContacts
      }
      if (this.localFilters.search) {
        filters.search = this.localFilters.search
      }
      const retour = await getContactsProgrammes(filters)
      if (retour && retour.type === 'error') {
        this.tableData = []
      } else {
        this.tableData = retour.data || []
      }
      this.loading = false
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/theme/_bootstrap.scss";

.contacts-programs-table-wrap {
  ::v-deep .el-table__body-wrapper {
    max-height: 600px;
  }
}

.contacts-programs-table {
  ::v-deep th {
    background-color: #f5f7fa;
    color: $black;
    font-weight: 600;
  }

  ::v-deep .contacts-programs-table__total-row td {
    background-color: #35616b !important;
    color: $white !important;
    font-weight: bold;
  }

  ::v-deep tbody tr:not(.contacts-programs-table__total-row):hover td {
    background-color: #ebf3f2;
  }
}
</style>

<style lang="scss">
@import "@/theme/_bootstrap.scss";

/*
 * Zone de contenu : marges latérales + padding intérieur du panel.
 * Le wrapper ne dépend pas du merge de classe sur PageContainer / wide-main-content.
 */
.ContactsPrograms__content {
  box-sizing: border-box;
  width: 95%;
  max-width: none;
  margin-left: auto;
  margin-right: auto;

  @include media-breakpoint-down(sm) {
    width: 100%;
    padding-left: map-get($spacers, 3);
    padding-right: map-get($spacers, 3);
  }

  /* Bandeau ApiSearchForm : fond transparent */
  ::v-deep .api-search-form .search-panel {
    background: transparent !important;
    background-color: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }

  ::v-deep .api-search-form .search-container {
    background-color: transparent !important;
  }

  .contacts-programs-panel.Panel--fullWidth {
    width: 100% !important;
    max-width: none !important;
  }

  /*
   * Surcharge .Panel--fullWidth .el-card__body { padding: 0 } (Panel.vue, global).
   * Sélecteur identique + ancêtre page pour gagner en spécificité / ordre de chargement.
   */
  .contacts-programs-panel.Panel--fullWidth .el-card__header {
    padding-left: map-get($spacers, 4) !important;
    padding-right: map-get($spacers, 4) !important;
  }

  /* h3 global : text-transform uppercase — conserver la casse de la traduction */
  // .contacts-programs-panel .Panel-headerTitle {
  //   text-transform: none;
  // }

  .contacts-programs-panel.Panel--fullWidth .el-card__body {
    padding-left: map-get($spacers, 4) !important;
    padding-right: map-get($spacers, 4) !important;
  }
}

/* Renfort si wide-main-content / PageContainer--wideMain est actif sur le conteneur */
.UserLayout .PageContainer.ContactsPrograms,
.UserLayout .PageContainer.ContactsPrograms.PageContainer--wideMain {
  @include media-breakpoint-up(md) {
    .PageContainer-main > .responsive-main {
      width: 95% !important;
      max-width: none !important;
      margin-left: auto !important;
      margin-right: auto !important;
    }
  }

  @include media-breakpoint-up(lg) {
    .PageContainer-main > .responsive-main {
      width: 95% !important;
      max-width: none !important;
      margin-left: auto !important;
      margin-right: auto !important;
    }
  }
}
</style>
